import { Mark, mergeAttributes } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    diffInsertion: {
      /**
       * Apply a diff insertion mark to a specific range.
       * Used to highlight text that was added (exists in current but not in original).
       */
      setDiffInsertion: (from: number, to: number) => ReturnType;
      /**
       * Remove all diff insertion marks from the document.
       */
      unsetAllDiffInsertions: () => ReturnType;
    };
  }
}

/**
 * TipTap Mark extension for highlighting inserted/added text in diff mode.
 *
 * This mark is used to visually indicate text ranges that were added
 * (present in current content but not in original content).
 *
 * Key features:
 * - Renders as a styled span with sky-600 color
 * - Does NOT serialize to markdown (ephemeral visual state only)
 * - inclusive: false prevents the mark from extending when user types at boundaries
 * - Can coexist with SuggestionHighlightMark
 *
 * Usage:
 * ```typescript
 * editor.commands.setDiffInsertion(10, 25);
 * ```
 */
export const DiffInsertionMark = Mark.create({
  name: "diffInsertion",

  // Don't extend the mark when typing at boundaries
  inclusive: false,

  // Don't keep the mark when splitting nodes
  keepOnSplit: false,

  // Allow overlap with other marks (suggestions, etc.)
  excludes: "",

  parseHTML() {
    return [
      {
        tag: "span.diff-insertion",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: "diff-insertion",
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setDiffInsertion:
        (from: number, to: number) =>
        ({ chain }) => {
          return chain()
            .setTextSelection({ from, to })
            .setMark(this.name)
            .setTextSelection(to) // Move cursor to end to avoid selection
            .run();
        },

      unsetAllDiffInsertions:
        () =>
        ({ tr, state }) => {
          const { doc } = state;

          // Find and remove all diff insertion marks
          doc.descendants((node, pos) => {
            if (!node.isText) return;

            const marks = node.marks.filter(
              (mark) => mark.type.name === this.name,
            );

            marks.forEach((mark) => {
              tr.removeMark(pos, pos + node.nodeSize, mark);
            });
          });

          return true;
        },
    };
  },
});
